/**
 * 統一エラーページ - 全システムエラーを集約表示
 *
 * @file UnifiedErrorPage.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 404、サービス無効、APIエラー等の全システムエラーを統一的に表示。
 * セキュリティ要件により、サービス名等の詳細は表示せず、
 * エラーコードのみで管理者が原因を特定できる設計。
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { Typography, Button, Container, Paper } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import type { UnifiedErrorPageProps } from '../../types/error';
import { formatErrorCode } from '../../utils/errorCodes';
import { getDisplaySettingsSnapshot } from '@/contexts/displaySettingsSnapshot';

export const UnifiedErrorPage: React.FC<UnifiedErrorPageProps> = ({
  errorCode,
  redirectUrl = '/',
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const redirectSeconds = getDisplaySettingsSnapshot().errorPageRedirectSeconds;
  const [countdown, setCountdown] = useState<number>(redirectSeconds);

  // リダイレクト処理
  const handleRedirect = useCallback(() => {
    navigate(redirectUrl);
  }, [navigate, redirectUrl]);

  // カウントダウン処理
  useEffect(() => {
    // 0未満の場合は即時リダイレクト
    if (redirectSeconds < 0) {
      handleRedirect();
      return;
    }

    // 0の場合は自動リダイレクトしない
    if (redirectSeconds === 0) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // クリーンアップ
    return () => {
      clearInterval(timer);
    };
  }, [redirectSeconds, handleRedirect]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        {/* タイトル */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 'bold', color: 'text.primary' }}
        >
          {t('errors.unified_error.title')}
        </Typography>

        {/* 説明文 */}
        <Typography
          variant="body1"
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('errors.unified_error.description')}
        </Typography>

        {/* ホームに戻るボタン */}
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<HomeIcon />}
          onClick={handleRedirect}
          sx={{ mb: 3 }}
        >
          {t('errors.unified_error.go_home')}
        </Button>

        {/* カウントダウン表示（自動リダイレクトが有効な場合のみ） */}
        {redirectSeconds > 0 && countdown > 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t('errors.unified_error.redirecting', { seconds: countdown })}
          </Typography>
        )}

        {/* エラーコード（管理者用：画面下部に小さく表示） */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            display: 'block',
            mt: 4,
            fontSize: '0.75rem',
          }}
        >
          {formatErrorCode(errorCode)}
        </Typography>
      </Paper>
    </Container>
  );
};

export default UnifiedErrorPage;
